import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  Alert,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Save, Add, Edit, Delete } from '@mui/icons-material';

import FaqFormDialog from './FaqFormDialog';
import { notify } from 'utils/notify';
import AboutUsEditor from './AboutUsEditor';
import cmsService, {
  faqItemsFromSettingsDoc,
  getSettingsDocument,
  pageModelFromSettingsDoc,
  type CmsPageType,
  type FaqItem,
  type CmsPageUpdatePayload,
  type DeleteCmsPageTarget,
  type SettingsApiData,
} from '../../services/cms.service';
import useModuleAccess from 'hooks/useModuleAccess';

const PAGE_TABS: { key: CmsPageType; label: string }[] = [
  { key: 'terms_conditions', label: 'Terms & Conditions' },
  { key: 'privacy_policy', label: 'Privacy Policy' },
  { key: 'faq', label: 'FAQ' },
  { key: 'about_us', label: 'About Us' },
];

const FAQ_TAB_INDEX = 2;
const ABOUT_TAB_INDEX = 3;

function axiosErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e.response?.data?.message ?? e.message ?? fallback;
}

function CmsLoadingState() {
  return (
    <Box display="flex" justifyContent="center" py={6}>
      <CircularProgress />
    </Box>
  );
}

type AboutCmsTabProps = {
  pageError: string;
  onDismissPageError: () => void;
  tabLabel: string;
  aboutRemountKey: number;
  initialHtml: string;
  onSettingsUpdated: (doc: SettingsApiData) => void;
  canEdit: boolean;
  canDelete: boolean;
  onDeleteAboutPage: () => void;
};

function AboutCmsTab({
  pageError,
  onDismissPageError,
  tabLabel,
  aboutRemountKey,
  initialHtml,
  onSettingsUpdated,
  canEdit,
  canDelete,
  onDeleteAboutPage,
}: Readonly<AboutCmsTabProps>) {
  return (
    <>
      {pageError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissPageError}>
          {pageError}
        </Alert>
      ) : null}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Page: {tabLabel}
      </Typography>
      <AboutUsEditor
        key={aboutRemountKey}
        initialHtml={initialHtml}
        onSettingsUpdated={onSettingsUpdated}
        canEdit={canEdit}
      />
      {canDelete ? (
        <Button
          size="small"
          color="error"
          variant="text"
          startIcon={<Delete fontSize="small" />}
          sx={{ mt: 2 }}
          onClick={onDeleteAboutPage}
        >
          Delete entire About Us page
        </Button>
      ) : null}
    </>
  );
}

type FaqCmsTabProps = {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canWriteFaq: boolean;
  faqs: FaqItem[];
  faqError: string;
  onDismissFaqError: () => void;
  onOpenCreate: () => void;
  onOpenEdit: (faq: FaqItem) => void;
  onRequestDelete: (faq: FaqItem) => void;
  dialogOpen: boolean;
  dialogMode: 'create' | 'edit';
  editingFaq: FaqItem | null;
  onCloseDialog: () => void;
  onFaqSubmit: (
    payload: Parameters<typeof cmsService.createFaq>[0] | Parameters<typeof cmsService.updateFaq>[1]
  ) => Promise<void>;
  faqToDelete: FaqItem | null;
  deleteSubmitting: boolean;
  onCloseDeleteDialog: () => void;
  onConfirmDeleteFaq: () => void;
};

function FaqCmsTab({
  canCreate,
  canUpdate,
  canDelete,
  canWriteFaq,
  faqs,
  faqError,
  onDismissFaqError,
  onOpenCreate,
  onOpenEdit,
  onRequestDelete,
  dialogOpen,
  dialogMode,
  editingFaq,
  onCloseDialog,
  onFaqSubmit,
  faqToDelete,
  deleteSubmitting,
  onCloseDeleteDialog,
  onConfirmDeleteFaq,
}: Readonly<FaqCmsTabProps>) {
  const faqActions = canUpdate || canDelete;

  let faqListBody: ReactNode;
  if (!canWriteFaq) {
    faqListBody = <Typography color="text.secondary">You do not have permission to create, edit, or delete FAQs.</Typography>;
  } else if (faqs.length === 0) {
    faqListBody = (
      <Typography color="text.secondary">No FAQs yet. Click &quot;Add FAQ&quot; to create one.</Typography>
    );
  } else {
    faqListBody = (
      <List>
        {faqs.map((faq) => (
          <ListItem
            key={faq.id}
            divider
            secondaryAction={
              faqActions ? (
                <Stack direction="row" spacing={0.5}>
                  {canUpdate ? (
                    <IconButton size="small" onClick={() => onOpenEdit(faq)} title="Edit">
                      <Edit fontSize="small" />
                    </IconButton>
                  ) : null}
                  {canDelete ? (
                    <IconButton size="small" onClick={() => onRequestDelete(faq)} title="Delete" color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  ) : null}
                </Stack>
              ) : null
            }
          >
            <ListItemText
              primary={faq.question}
              secondary={
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }} noWrap>
                    {faq.answer}
                  </Typography>
                  {faq.isActive === false ? <Chip label="Inactive" size="small" color="default" /> : null}
                </Stack>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Manage FAQ questions and answers from the <code>faq</code> array on settings.
        </Typography>
        {canCreate ? (
          <Button variant="contained" startIcon={<Add />} onClick={onOpenCreate}>
            Add FAQ
          </Button>
        ) : null}
      </Stack>

      {faqError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissFaqError}>
          {faqError}
        </Alert>
      ) : null}

      {faqListBody}

      <FaqFormDialog
        open={dialogOpen}
        mode={dialogMode}
        faq={editingFaq ?? undefined}
        onClose={onCloseDialog}
        onSubmit={onFaqSubmit}
      />

      <Dialog open={Boolean(faqToDelete)} onClose={() => !deleteSubmitting && onCloseDeleteDialog()} maxWidth="xs" fullWidth>
        <DialogTitle>Delete FAQ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this FAQ? This cannot be undone.
            {faqToDelete?.question ? (
              <>
                <br />
                <br />
                <Typography component="span" variant="body2" fontWeight={600}>
                  {faqToDelete.question}
                </Typography>
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseDeleteDialog} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteFaq} disabled={deleteSubmitting}>
            {deleteSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

type LegalCmsTabProps = {
  pageError: string;
  onDismissPageError: () => void;
  tabLabel: string;
  pageContent: string;
  onContentChange: (content: string) => void;
  canMutateContent: boolean;
  saving: boolean;
  canSaveLegal: boolean;
  legalSaveLabel: string;
  onSave: () => void;
  cmsPageId: string | null;
  canDelete: boolean;
  onRequestDeleteLegalPage: () => void;
};

function LegalCmsTab({
  pageError,
  onDismissPageError,
  tabLabel,
  pageContent,
  onContentChange,
  canMutateContent,
  saving,
  canSaveLegal,
  legalSaveLabel,
  onSave,
  cmsPageId,
  canDelete,
  onRequestDeleteLegalPage,
}: Readonly<LegalCmsTabProps>) {
  const showDeleteLegal = Boolean(cmsPageId && canDelete);
  const caption = cmsPageId
    ? 'Click Update after you change the body.'
    : 'Save changes creates the page; after that, use Update.';

  return (
    <>
      {pageError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissPageError}>
          {pageError}
        </Alert>
      ) : null}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Page: {tabLabel}
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Body (HTML)
        </Typography>
        {canMutateContent ? (
          <IconButton
            size="small"
            color="error"
            title="Clear body content"
            aria-label="Clear body content"
            disabled={!pageContent.trim() || saving}
            onClick={() => onContentChange('')}
          >
            <Delete fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
      <TextField
        fullWidth
        multiline
        rows={18}
        value={pageContent}
        onChange={(e) => onContentChange(e.target.value)}
        placeholder="Enter HTML content..."
        slotProps={{
          input: { readOnly: !canMutateContent },
        }}
        sx={{
          mb: 2,
          '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 },
        }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Stack spacing={0.5} alignItems="flex-start" sx={{ maxWidth: { xs: '100%', sm: '60%' } }}>
          <Typography variant="caption" color="text.secondary">
            Supports HTML formatting. {caption}
          </Typography>
          {showDeleteLegal ? (
            <Button
              size="small"
              color="error"
              variant="text"
              startIcon={<Delete fontSize="small" />}
              onClick={onRequestDeleteLegalPage}
            >
              Delete entire page
            </Button>
          ) : null}
        </Stack>
        <Button variant="contained" startIcon={<Save />} onClick={onSave} disabled={saving || !canSaveLegal}>
          {saving ? 'Saving...' : legalSaveLabel}
        </Button>
      </Box>
    </>
  );
}

type DeleteCmsPageDialogProps = {
  open: boolean;
  submitting: boolean;
  targetLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
};

function DeleteCmsPageDialog({ open, submitting, targetLabel, onClose, onConfirm }: Readonly<DeleteCmsPageDialogProps>) {
  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Delete CMS page</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This removes the stored page from the database. You can recreate it later with Save or Update.
          {targetLabel ? (
            <>
              <br />
              <br />
              <Typography component="span" variant="body2" fontWeight={600}>
                {targetLabel}
              </Typography>
            </>
          ) : null}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={submitting}>
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type RenderTabBodyParams = {
  contentBusy: boolean;
  isAboutTab: boolean;
  isFaqTab: boolean;
  aboutProps: AboutCmsTabProps;
  faqProps: FaqCmsTabProps;
  legalProps: LegalCmsTabProps;
};

function renderTabBody(params: Readonly<RenderTabBodyParams>): ReactNode {
  if (params.contentBusy) {
    return <CmsLoadingState />;
  }
  if (params.isAboutTab) {
    return <AboutCmsTab {...params.aboutProps} />;
  }
  if (params.isFaqTab) {
    return <FaqCmsTab {...params.faqProps} />;
  }
  return <LegalCmsTab {...params.legalProps} />;
}

export default function CMSManagement() {
  const { canCreate, canUpdate, canDelete } = useModuleAccess('cms');
  const canMutateContent = canCreate || canUpdate;
  const canWriteFaq = canCreate || canUpdate || canDelete;

  const [tab, setTab] = useState(0);
  const [settingsDoc, setSettingsDoc] = useState<SettingsApiData | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState('');

  const [pageContent, setPageContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveIsUpdate, setSaveIsUpdate] = useState(false);
  const [pageError, setPageError] = useState('');

  const [cmsPageId, setCmsPageId] = useState<string | null>(null);
  const [baselineContent, setBaselineContent] = useState('');

  const [faqError, setFaqError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
  const [faqToDelete, setFaqToDelete] = useState<FaqItem | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [cmsPageDeleteTarget, setCmsPageDeleteTarget] = useState<(DeleteCmsPageTarget & { label: string }) | null>(null);
  const [cmsPageDeleteSubmitting, setCmsPageDeleteSubmitting] = useState(false);
  const [cmsPageDeleted, setCmsPageDeleted] = useState(false);
  const [aboutRemountKey, setAboutRemountKey] = useState(0);

  const currentPageType = PAGE_TABS[tab]?.key;
  const isFaqTab = tab === FAQ_TAB_INDEX;
  const isAboutTab = tab === ABOUT_TAB_INDEX;

  const faqs = useMemo<FaqItem[]>(() => {
    if (!settingsDoc) return [];
    return faqItemsFromSettingsDoc(settingsDoc);
  }, [settingsDoc]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSettingsLoading(true);
      setSettingsError('');
      try {
        const doc = await getSettingsDocument();
        if (!cancelled) setSettingsDoc(doc);
      } catch {
        if (!cancelled) setSettingsError('Failed to load CMS settings');
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!settingsDoc || isFaqTab || isAboutTab) return;
    const pt = currentPageType;
    if (pt !== 'terms_conditions' && pt !== 'privacy_policy') return;
    const model = pageModelFromSettingsDoc(settingsDoc, pt);
    setPageContent(model.content);
    setBaselineContent(model.content);
    setCmsPageId(model.id);
  }, [settingsDoc, tab, currentPageType, isFaqTab, isAboutTab]);

  const handleContentChange = (content: string) => {
    setPageContent(content);
    setSaved(false);
  };

  const legalPageDirty = pageContent !== baselineContent;

  const handleSave = async () => {
    if (!currentPageType || isFaqTab || isAboutTab) return;
    if (!legalPageDirty) return;
    setSaving(true);
    setPageError('');
    try {
      const payload: CmsPageUpdatePayload = { content: pageContent };
      const updating = Boolean(cmsPageId);
      const doc = await cmsService.updatePage(currentPageType, payload, { pageId: cmsPageId });
      setSettingsDoc(doc);
      setSaveIsUpdate(updating);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      setPageError(axiosErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const legalSaveLabel = cmsPageId ? 'Update' : 'Save changes';
  const canSaveLegal =
    Boolean(currentPageType && !isFaqTab && !isAboutTab) && legalPageDirty && (cmsPageId ? canUpdate : canCreate);

  const handleOpenCreateFaq = () => {
    setDialogMode('create');
    setEditingFaq(null);
    setDialogOpen(true);
  };

  const handleOpenEditFaq = (faq: FaqItem) => {
    setDialogMode('edit');
    setEditingFaq(faq);
    setDialogOpen(true);
  };

  const handleFaqSubmit = async (
    payload: Parameters<typeof cmsService.createFaq>[0] | Parameters<typeof cmsService.updateFaq>[1]
  ) => {
    if (dialogMode === 'create') {
      const doc = await cmsService.createFaq(payload as Parameters<typeof cmsService.createFaq>[0]);
      setSettingsDoc(doc);
      notify.success('FAQ added successfully.');
      return;
    }
    if (!editingFaq?.id?.trim()) {
      throw new Error('FAQ id is missing. Refresh the page and try again.');
    }
    const doc = await cmsService.updateFaq(editingFaq.id, payload as Parameters<typeof cmsService.updateFaq>[1]);
    setSettingsDoc(doc);
    notify.success('FAQ updated successfully.');
  };

  const handleConfirmDeleteFaq = async () => {
    if (!faqToDelete?.id?.trim()) {
      setFaqError('FAQ id is missing. Refresh the page and try again.');
      return;
    }
    setDeleteSubmitting(true);
    setFaqError('');
    try {
      const doc = await cmsService.deleteFaq(faqToDelete.id);
      setSettingsDoc(doc);
      setFaqToDelete(null);
      notify.success('FAQ deleted successfully.');
    } catch (err: unknown) {
      setFaqError(axiosErrorMessage(err, 'Failed to delete FAQ'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleConfirmDeleteCmsPage = async () => {
    if (!cmsPageDeleteTarget) return;
    setCmsPageDeleteSubmitting(true);
    setPageError('');
    try {
      const doc = await cmsService.deletePage({
        id: cmsPageDeleteTarget.id,
        pageType: cmsPageDeleteTarget.pageType,
      });
      setSettingsDoc(doc);
      setCmsPageDeleteTarget(null);
      setCmsPageDeleted(true);
      setTimeout(() => setCmsPageDeleted(false), 4000);
      if (currentPageType === 'about_us') {
        setAboutRemountKey((k) => k + 1);
      }
    } catch (err: unknown) {
      setPageError(axiosErrorMessage(err, 'Failed to delete page'));
    } finally {
      setCmsPageDeleteSubmitting(false);
    }
  };

  const contentBusy = settingsLoading && !settingsDoc;
  const tabLabel = PAGE_TABS[tab]?.label ?? '';

  const requestDeleteLegalPage = () => {
    if (!cmsPageId || !currentPageType) return;
    setCmsPageDeleteTarget({
      id: cmsPageId,
      label: PAGE_TABS[tab]?.label ?? currentPageType,
      pageType: currentPageType,
    });
  };

  const mainPanel = renderTabBody({
    contentBusy,
    isAboutTab,
    isFaqTab,
    aboutProps: {
      pageError,
      onDismissPageError: () => setPageError(''),
      tabLabel,
      aboutRemountKey,
      initialHtml: settingsDoc?.about ?? '',
      onSettingsUpdated: setSettingsDoc,
      canEdit: canMutateContent,
      canDelete,
      onDeleteAboutPage: () =>
        setCmsPageDeleteTarget({ id: 'about_us', label: 'About Us', pageType: 'about_us' }),
    },
    faqProps: {
      canCreate,
      canUpdate,
      canDelete,
      canWriteFaq,
      faqs,
      faqError,
      onDismissFaqError: () => setFaqError(''),
      onOpenCreate: handleOpenCreateFaq,
      onOpenEdit: handleOpenEditFaq,
      onRequestDelete: setFaqToDelete,
      dialogOpen,
      dialogMode,
      editingFaq,
      onCloseDialog: () => setDialogOpen(false),
      onFaqSubmit: handleFaqSubmit,
      faqToDelete,
      deleteSubmitting,
      onCloseDeleteDialog: () => setFaqToDelete(null),
      onConfirmDeleteFaq: () => {
        void handleConfirmDeleteFaq();
      },
    },
    legalProps: {
      pageError,
      onDismissPageError: () => setPageError(''),
      tabLabel,
      pageContent,
      onContentChange: handleContentChange,
      canMutateContent,
      saving,
      canSaveLegal,
      legalSaveLabel,
      onSave: () => {
        void handleSave();
      },
      cmsPageId,
      canDelete,
      onRequestDeleteLegalPage: requestDeleteLegalPage,
    },
  });

  return (
    <Box>
      {saved ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveIsUpdate ? 'Page updated successfully!' : 'Content saved successfully!'}
        </Alert>
      ) : null}

      {cmsPageDeleted ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCmsPageDeleted(false)}>
          CMS page deleted. You can recreate it later by saving again.
        </Alert>
      ) : null}

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          {PAGE_TABS.map((p) => (
            <Tab key={p.key} label={p.label} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {settingsError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSettingsError('')}>
              {settingsError}
            </Alert>
          ) : null}

          {mainPanel}
        </Box>
      </Paper>

      <DeleteCmsPageDialog
        open={Boolean(cmsPageDeleteTarget)}
        submitting={cmsPageDeleteSubmitting}
        targetLabel={cmsPageDeleteTarget?.label}
        onClose={() => setCmsPageDeleteTarget(null)}
        onConfirm={() => {
          void handleConfirmDeleteCmsPage();
        }}
      />
    </Box>
  );
}
